// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { ChevronDown, Columns3, Search } from "lucide-react"
import type { ColumnDef } from "@tanstack/react-table"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/**
 * The toolbar of the My Forges table — the live name search on the left,
 * the column-visibility popover on the right, per the frozen design.
 *
 * The toolbar lives in the view (not in `SmartTable`) so it stays on
 * screen in the filtered-no-match state, where the table is replaced by
 * the "no forges match" panel but the query must remain editable.
 */

export const ForgesSearchField = ({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) => (
  <div className="flex h-[38px] w-full max-w-[300px] items-center gap-2 rounded-[10px] border border-border-strong bg-surface px-3 transition-[border-color,box-shadow] duration-200 focus-within:border-border-active focus-within:shadow-[0_0_0_3px_rgba(69,240,200,0.14)]">
    <Search aria-hidden="true" className="size-[15px] shrink-0 text-text-tertiary" />
    <input
      type="search"
      aria-label="Search forges by name"
      placeholder="Search forges by name…"
      className="w-full border-none bg-transparent text-[13px] text-text-primary outline-none placeholder:text-text-muted"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
)

/**
 * "Columns" button + popover, controlling the view-owned visibility map
 * (`SmartTable` consumes it in controlled mode). Only the togglable
 * columns are listed — `name` and `best_score` are the anchors.
 */
export const ColumnsMenu = ({
  columns,
  visibleColumns,
  onToggle,
}: {
  columns: ColumnDef<Forge>[]
  visibleColumns: Record<string, boolean>
  onToggle: (key: string) => void
}) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className="flex h-[38px] items-center gap-2 rounded-[10px] border border-border-strong bg-[rgba(7,30,35,0.4)] px-3.5 text-[13px] text-text-secondary transition-colors duration-200 hover:border-border-active hover:text-text-primary"
      >
        <Columns3 aria-hidden="true" className="size-[15px]" />
        Columns
        <ChevronDown aria-hidden="true" className="size-[13px]" />
      </button>
    </PopoverTrigger>
    <PopoverContent
      align="end"
      sideOffset={6}
      className="w-[230px] animate-box-in rounded-xl border-border-strong bg-popover p-[7px] shadow-[var(--shadow-popover)]"
    >
      <div className="px-[9px] pt-1.5 pb-2 font-mono text-[9.5px] tracking-[0.1em] text-text-tertiary uppercase">
        Visible columns
      </div>
      <div className="flex flex-col">
        {columns
          .filter((column) => column.enableHiding !== false)
          .map((column) => {
            const key = column.id as string
            return (
              <label
                key={key}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-[9px] py-[7px] text-[13px] text-text-secondary transition-colors duration-150 hover:bg-[rgba(69,240,200,0.05)] hover:text-text-primary"
              >
                <Checkbox
                  id={`col-toggle-${key}`}
                  checked={visibleColumns[key]}
                  onCheckedChange={() => onToggle(key)}
                  className="rounded-[5px] border-border-active data-[state=checked]:border-primary"
                />
                <span>{String(column.header ?? key)}</span>
              </label>
            )
          })}
      </div>
    </PopoverContent>
  </Popover>
)
